import { inject, Injectable } from '@angular/core';
import {
    LookupApiService,
    SelectApiService,
    TagApiService,
} from '@wdx/clmi/api-services/services';
import {
    LogicStepOption,
    LogicStepParameter,
    LogicStepParameterType,
} from '@wdx/shared/utils';
import { WorkflowDesignerFacade } from './workflow-designer.facade';
import { Observable, of, map, switchMap, tap } from 'rxjs';
import { LogicstepCodeParameterParameterValuesApiService } from '@wdx/clmi/clmi-swagger-gen/api-services';

export type OptionsResponse = {
    id?: string | null;
    name?: string | null;
    code?: string | null;
};

export type ParameterConfig = Partial<
    Record<
        LogicStepParameterType,
        {
            cacheKey: (param: LogicStepParameter) => string;
            apiCall: (
                param: LogicStepParameter,
            ) => Observable<OptionsResponse[]>;
        }
    >
>;

@Injectable({
    providedIn: 'root',
})
export class WorkflowParameterOptionsService {
    private readonly selectApiService = inject(SelectApiService);
    private readonly lookupApiService = inject(LookupApiService);
    private readonly tagApiService = inject(TagApiService);
    private readonly logicStepParameterValuesApiService = inject(
        LogicstepCodeParameterParameterValuesApiService,
    );
    private readonly facade = inject(WorkflowDesignerFacade);

    private optionsCache = new Map<string, LogicStepOption[]>();

    private readonly parameterConfig: ParameterConfig = {
        [LogicStepParameterType.Select]: {
            cacheKey: (param) => `select-${param.selectApiSource}`,
            apiCall: (param) =>
                this.selectApiService.getSelect(param.selectApiSource),
        },
        [LogicStepParameterType.Lookup]: {
            cacheKey: (param) => `lookup-${param.lookupSourceType}`,
            apiCall: (param) =>
                this.lookupApiService.getLookup(param.lookupSourceType),
        },
        [LogicStepParameterType.Tag]: {
            cacheKey: (param) =>
                param.tagCategory ? `tag-${param.tagCategory}` : `tag-all`,
            apiCall: (param) =>
                param.tagCategory
                    ? this.tagApiService
                          .getForCategory({ type: param.tagCategory })
                          .pipe(
                              map((tags) =>
                                  tags.map((t) => ({ id: t.id, name: t.tag })),
                              ),
                          )
                    : this.tagApiService
                          .getAllTags()
                          .pipe(map((tags) => tags.flatMap((t) => t.tags))),
        },
        [LogicStepParameterType.ApiSource]: {
            cacheKey: (param) => `apiSource-${param.code}`,
            apiCall: () => of([]),
        },
    };

    getOptions(param: LogicStepParameter): Observable<LogicStepOption[]> {
        const config = this.parameterConfig[param.kind];

        if (!config) {
            return of([]);
        }

        if (param.kind === LogicStepParameterType.ApiSource) {
            return this.facade.workflowContext$.pipe(
                switchMap((context) =>
                    this.facade.resolveEntityTypeForNode$(context.node).pipe(
                        switchMap((targetEntityType) => {
                            const cacheKey = `${config.cacheKey(param)}-${targetEntityType}`;

                            if (this.optionsCache.has(cacheKey)) {
                                return of(this.optionsCache.get(cacheKey)!);
                            }

                            if (!context.node?.code) {
                                return of([]);
                            }

                            return this.logicStepParameterValuesApiService
                                .get(
                                    context.node.code,
                                    param.code,
                                    targetEntityType,
                                )
                                .pipe(
                                    map((attributes) =>
                                        attributes.map((attr) => ({
                                            value: attr.value,
                                            label: attr.label,
                                        })),
                                    ),
                                    tap((options) => {
                                        this.optionsCache.set(
                                            cacheKey,
                                            options,
                                        );
                                    }),
                                );
                        }),
                    ),
                ),
            );
        }

        const cacheKey = config.cacheKey(param);

        if (this.optionsCache.has(cacheKey)) {
            return of(this.optionsCache.get(cacheKey)!);
        }

        return config.apiCall(param).pipe(
            map((results) =>
                results.map(({ id, name, code }) => ({
                    value: code ?? id,
                    label: name,
                })),
            ),
            tap((options) => {
                this.optionsCache.set(cacheKey, options);
            }),
        );
    }
}
